// Gerador de comandos ESC/POS para impressora térmica de cupom (58mm / 32 colunas).
// Produz apenas os bytes (Array<number>) do recibo; o envio é feito pela camada
// de transporte (Bluetooth nativo do Fully Kiosk) em utils/print.js.
//
// Alternativa de biblioteca: se preferir não manter este encoder, o pacote
// `esc-pos-encoder` (NielsLeenheer) gera um Uint8Array de comandos ESC/POS 100%
// no navegador; basta trocar as funções `montarRecibo*` por chamadas a ele e
// repassar os bytes resultantes para `imprimirBytes()` em utils/print.js.

const LARGURA = 32; // colunas em papel 58mm (fonte A)

// --- Comandos base ESC/POS ---
const ESC = 0x1b;
const GS = 0x1d;
const INIT = [ESC, 0x40]; // ESC @  -> reinicia a impressora
const ALINHA_ESQ = [ESC, 0x61, 0x00];
const ALINHA_CENTRO = [ESC, 0x61, 0x01];
const NEGRITO_ON = [ESC, 0x45, 0x01];
const NEGRITO_OFF = [ESC, 0x45, 0x00];
const TAMANHO_NORMAL = [GS, 0x21, 0x00]; // GS ! 0
const TAMANHO_DUPLO = [GS, 0x21, 0x11]; // GS ! (dobra largura + altura)

// Remove acentos/diacríticos e caracteres não-ASCII para impressão confiável
// em impressoras térmicas baratas (evita "lixo" por code page divergente).
function ascii(s) {
  return String(s ?? '')
    .replace(/[\u00a0\u202f\u2007]/g, ' ') // espaços não-separáveis (ex.: "R$ 10,00") viram espaço normal
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7e]/g, '?');
}

// Converte texto ASCII em bytes.
function texto(bytes, s) {
  const a = ascii(s);
  for (let i = 0; i < a.length; i++) bytes.push(a.charCodeAt(i) & 0xff);
}

function novaLinha(bytes, n = 1) {
  for (let i = 0; i < n; i++) bytes.push(0x0a);
}

// Linha com texto à esquerda e à direita dentro da largura do papel.
function colunas(bytes, esq, dir) {
  let e = ascii(esq);
  const d = ascii(dir);
  if (e.length + d.length + 1 > LARGURA) {
    e = e.slice(0, LARGURA - d.length - 1);
  }
  const espacos = Math.max(1, LARGURA - e.length - d.length);
  texto(bytes, e + ' '.repeat(espacos) + d);
  novaLinha(bytes);
}

function separador(bytes) {
  texto(bytes, '-'.repeat(LARGURA));
  novaLinha(bytes);
}

function brl(v) {
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function cabecalho(bytes, estabelecimento, titulo) {
  bytes.push(...INIT);
  bytes.push(...ALINHA_CENTRO);
  bytes.push(...NEGRITO_ON, ...TAMANHO_DUPLO);
  texto(bytes, estabelecimento);
  novaLinha(bytes);
  bytes.push(...TAMANHO_NORMAL);
  texto(bytes, titulo);
  novaLinha(bytes);
  bytes.push(...NEGRITO_OFF);
  texto(bytes, new Date().toLocaleString('pt-BR'));
  novaLinha(bytes);
  bytes.push(...ALINHA_ESQ);
  separador(bytes);
}

function rodape(bytes, textoRodape) {
  if (textoRodape) {
    bytes.push(...ALINHA_CENTRO);
    texto(bytes, textoRodape);
    novaLinha(bytes);
    bytes.push(...ALINHA_ESQ);
  }
  // Avanço para permitir destacar o papel (impressoras 58mm geralmente não têm
  // guilhotina, por isso alimentamos linhas em vez de enviar comando de corte).
  novaLinha(bytes, 4);
}

function itemLinha(bytes, it, mostrarPrecos) {
  const qtd = it.quantidade ?? it.qtd ?? 1;
  const nome = `${qtd}x ${it.nome || it.produto_nome || ''}`;
  const preco = mostrarPrecos && it.subtotal != null ? brl(it.subtotal) : '';
  if (preco) {
    colunas(bytes, nome, preco);
  } else {
    texto(bytes, ascii(nome).slice(0, LARGURA));
    novaLinha(bytes);
  }
  if (it.observacao) {
    texto(bytes, '  > ' + ascii(it.observacao));
    novaLinha(bytes);
  }
}

/**
 * Monta os bytes ESC/POS de um cupom de comanda/produção.
 * Mesmos campos aceitos por imprimirComanda() em utils/print.js.
 * @returns {number[]} bytes do recibo
 */
export function montarReciboComanda({
  titulo = 'COMANDA',
  estabelecimento = 'Parada Predileta',
  mesa,
  comanda,
  cliente,
  origem,
  itens = [],
  total = null,
  mostrarPrecos = true,
  rodape: rodapeTexto = '',
} = {}) {
  const bytes = [];
  cabecalho(bytes, estabelecimento, titulo);

  if (origem != null) colunas(bytes, 'Origem:', String(origem));
  if (mesa != null) colunas(bytes, 'Mesa:', String(mesa));
  if (comanda != null) colunas(bytes, 'Comanda:', `#${comanda}`);
  if (cliente) colunas(bytes, 'Cliente:', String(cliente));
  separador(bytes);

  if (itens.length === 0) {
    bytes.push(...ALINHA_CENTRO);
    texto(bytes, 'Sem itens');
    novaLinha(bytes);
    bytes.push(...ALINHA_ESQ);
  } else {
    itens.forEach((it) => itemLinha(bytes, it, mostrarPrecos));
  }

  if (mostrarPrecos && total != null) {
    separador(bytes);
    bytes.push(...NEGRITO_ON);
    colunas(bytes, 'TOTAL', brl(total));
    bytes.push(...NEGRITO_OFF);
  }

  rodape(bytes, rodapeTexto);
  return bytes;
}

/**
 * Monta os bytes ESC/POS do cupom de cobrança (todos os pedidos de uma mesa).
 * Mesmos campos aceitos por imprimirCobranca() em utils/print.js.
 * @returns {number[]} bytes do recibo
 */
export function montarReciboCobranca({
  titulo = 'PEDIDOS A COBRAR',
  estabelecimento = 'Parada Predileta',
  mesa,
  grupos = [],
  balcaoItens = [],
  total = null,
} = {}) {
  const bytes = [];
  cabecalho(bytes, estabelecimento, titulo);

  if (mesa != null) colunas(bytes, 'Mesa:', String(mesa));
  separador(bytes);

  grupos.forEach((g, idx) => {
    if (idx > 0) separador(bytes);
    const cab = [
      g.pedido_numero != null ? `Pedido #${g.pedido_numero}` : '',
      g.comanda_numero != null ? `Comanda #${g.comanda_numero}` : '',
    ].filter(Boolean).join(' ');
    if (cab) {
      bytes.push(...NEGRITO_ON);
      texto(bytes, ascii(cab).slice(0, LARGURA));
      novaLinha(bytes);
      bytes.push(...NEGRITO_OFF);
    }
    if (g.cliente_nome) {
      texto(bytes, 'Cliente: ' + ascii(g.cliente_nome));
      novaLinha(bytes);
    }
    const itens = g.itens || [];
    if (itens.length === 0) {
      texto(bytes, 'Sem itens');
      novaLinha(bytes);
    } else {
      itens.forEach((it) => itemLinha(bytes, it, true));
    }
  });

  if ((balcaoItens || []).length > 0) {
    if (grupos.length) separador(bytes);
    bytes.push(...NEGRITO_ON);
    texto(bytes, 'Balcao');
    novaLinha(bytes);
    bytes.push(...NEGRITO_OFF);
    balcaoItens.forEach((it) => itemLinha(bytes, it, true));
  }

  if (total != null) {
    separador(bytes);
    bytes.push(...NEGRITO_ON);
    colunas(bytes, 'TOTAL', brl(total));
    bytes.push(...NEGRITO_OFF);
  }

  rodape(bytes, '');
  return bytes;
}
